import React, { useState, useCallback, useEffect, useRef } from "react";
import { Tag, Button, Drawer, Spin, notification, Table, theme } from "antd";
import {
  CloseOutlined,
  ArrowLeftOutlined,
  TrophyFilled,
} from "@ant-design/icons";
import CustomChart from "../Charts";
import { formatAppNumber } from "util/locale";
import styles from "./VOCScoreChart.module.scss";
import GaugeChart from "react-gauge-chart";
import { useTranslation } from "react-i18next";

// NEW: fetch + reviews UI
import { apiRequest } from "util/services";
import WelcomeDrawerReviewsList from "pages/Welcome/WelcomeDrawerReviewsList";
import { set } from "ol/transform";

function getBrandTooltipDetails({ data, brandIndex = 0, brandNameOverride, t, language }) {
  if (!data?.brandsScore) return [];
  try {
    var brand = data.brandsScore;
    var brandName =
      brandNameOverride ||
      (brand.labels && brand.labels[brandIndex]
        ? brand.labels[brandIndex]
        : "Brand");
    var score =
      brand.datasets && brand.datasets[0] && brand.datasets[0].data
        ? brand.datasets[0].data[brandIndex]
        : 0;
    var details = [
      (t ? t("welcome_page.brand", "Brand") : "Brand") + ": " + brandName,
      (t ? t("welcome_page.voc_score", "VOC Score") : "VOC Score") + ": " + score.toFixed(1),
    ];
    var totalReviews = null;
    var topicDetails = [];
    var appRatingScore = null;
    var parentTopicsAvgVOC = null;
    if (brand.metadata) {
      appRatingScore = brand.metadata.appRatingScores
        ? brand.metadata.appRatingScores[brandIndex]
        : null;
      parentTopicsAvgVOC = brand.metadata.originalScores
        ? brand.metadata.originalScores[brandIndex]
        : null;
    }
    if (data && data.topicsScore && data.topicsScore.allBrands) {
      var found = data.topicsScore.allBrands.find(function(b) {
        return b.brandName === brandName;
      });
      if (
        found &&
        found.data &&
        found.data.topicDetails &&
        found.data.topicDetails.length > 0
      ) {
        totalReviews = found.data.topicDetails.reduce(function(acc, t) {
          return acc + (t.total_reviews || 0);
        }, 0);
        topicDetails = found.data.topicDetails.slice().sort(function(a, b) {
          return b.score - a.score;
        });
      }
    }
    if (
      brand.datasets &&
      brand.datasets[0] &&
      brand.datasets[0].total_reviews
    ) {
      totalReviews = brand.datasets[0].total_reviews[brandIndex];
    }
    if (typeof totalReviews === "number") {
      details.push(
        (t ? t("welcome_page.total_reviews", "Total Reviews") : "Total Reviews") +
          ": " +
          formatAppNumber(totalReviews, language || "en")
      );
    }
    if (appRatingScore !== null && appRatingScore !== undefined) {
      details.push("App Rating Score: " + appRatingScore.toFixed(2));
    }
    if (parentTopicsAvgVOC !== null && parentTopicsAvgVOC !== undefined) {
      details.push(
        "Parent Topics Avg VOC Score: " + parentTopicsAvgVOC.toFixed(2)
      );
    }
    details.push("Weighting: 50% Parent Topics Avg VOC + 50% App Rating");
    if (topicDetails.length > 0) {
      details.push("");
      details.push("Parent Topics:");
      topicDetails.forEach((topic) => {
        details.push(`  • ${topic.name}: ${topic.score.toFixed(1)}`);
      });
    }
    return details;
  } catch (error) {
    console.error("Error generating brand tooltip content:", error);
    return ["Details unavailable"];
  }
}

const HighestScoreComponent = ({ brandScoreInfo, theme }) => {
  const { t } = useTranslation();
  const brandsScore = brandScoreInfo?.datasets[0]?.data || [];
  const labels = brandScoreInfo?.labels || [];

  // checking if all scores are the same
  const allScoresSame = brandsScore?.every((score) => score === brandsScore[0]);

  if (brandsScore?.length <= 1 || allScoresSame) {
    return null;
  }

  const highestScoreIndex = brandsScore?.indexOf(Math.max(...brandsScore));
  const highestScore = Math.max(...brandsScore);

  return brandsScore?.length > 1 ? (
    <div className={styles.highestScoreBox}>
      <span>{t("label.22")}:</span>
      <span className={styles.trophyBox}>
        <TrophyFilled
          style={{ fontSize: "24px" }}
          className={styles.trophyIcon}
        />
      </span>
      <span className={styles.brandLabel}>
        {labels[highestScoreIndex] || ""}
      </span>
      <span className={styles.brandScore}>{highestScore || ""}</span>
    </div>
  ) : (
    <></>
  );
};

const VOCScoreChart = ({
  data,
  theme,
  deepDiveData,
  onBrandClick,
  onExitDeepDive,
  isSingleBrand,
  widget_id,
  since,
  until,
  source = "welcome",
}) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [internalDeepDiveData, setInternalDeepDiveData] = useState(null);
  const [selectedBrandLabel, setSelectedBrandLabel] = useState(null);
  const [gaugeTooltipVisible, setGaugeTooltipVisible] = useState(false);

  // NEW: side panel state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [issueReviews, setIssueReviews] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [reviewsStartIndex, setReviewsStartIndex] = useState(0);
  const [reviewsPageSize] = useState(10);
  const [totalReviewsCount, setTotalReviewsCount] = useState(0);

  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [selectedBarData, setSelectedBarData] = useState(null);
  const [topProblems, setTopProblems] = useState([]);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [problemReviews, setProblemReviews] = useState([]);
  const [topProblemsLoading, setTopProblemsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [problemsPerPage] = useState(5);
  const [currentStepId, setCurrentStepId] = useState(1);
  const [totalReviews, setTotalReviews] = useState(0);
  const [selectedPerPage, setSelectedPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);

  // Fetch top problems from API
  const fetchTopProblems = async (payload) => {
    setTopProblemsLoading(true);
    try {
      const result = await apiRequest(
        "dashboards/voc/ai_insights",
        "POST",
        payload,
        true
      );

      if (!result.error && result.success) {
        setTopProblems(result?.success || []);
      } else {
        console.error("API error:", result.error);
        notification.error({
          message: "Error",
          description: result.error || "Failed to fetch top problems",
        });
      }
    } catch (error) {
      console.error("Error fetching top problems:", error);
      notification.error({
        message: "Error",
        description: "Failed to fetch top problems. Please try again.",
      });
    } finally {
      setTopProblemsLoading(false);
    }
  };

  // Fetch reviews for a specific problem
  const fetchProblemReviews = async (problem) => {
    setSelectedProblem(problem); // Set selected problem for row highlighting
    setReviewsLoading(true);
    try {
      console.log("Fetching reviews for problem:", problem);

      // Validate required problem data
      if (!problem || (!problem.rc_id && !problem.id)) {
        throw new Error("Invalid problem data: missing rc_id or id");
      }

      const result = await apiRequest(
        "dashboards/root-cause/reviews",
        "POST",
        {
          rc_id: problem.rc_id || problem.id,
          topic_id: problem.topic_id || null,
          dash_id: problem.dash_id || null,
        },
        true
      );

      console.log("Reviews API response received:", result);

      if (result?.success) {
        // Handle different response formats
        let reviewsData = [];
        let totalCount = 0;

        reviewsData = result.success;
        totalCount = result.success.length;

        setProblemReviews(reviewsData);
        setTotalReviews(totalCount);
      }
    } catch (error) {
      console.error("Error fetching problem reviews:", error);
      notification.error({
        message: "Error",
        description: "Failed to fetch reviews. Please try again.",
      });
      setTotalReviews(0);
      setProblemReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  // Handle problem click to show reviews
  const handleProblemClick = (problem) => {
    setSelectedProblem(problem);
    setProblemReviews([]); // Clear previous reviews

    // Fetch reviews from API
    fetchProblemReviews(problem);
  };
  // Close side panel
  const handleCloseSidePanel = () => {
    setSidePanelOpen(false);
    setDrawerOpen(false);
    setSelectedBarData(null);
    setSelectedProblem(null);
    setProblemReviews([]);
    setTotalReviews(0);
    setCurrentPage(1);
  };
  const handleGaugeMouseEnter = useCallback(function(event) {
    setGaugeTooltipVisible(true);
  }, []);

  const handleGaugeMouseLeave = useCallback(function() {
    setGaugeTooltipVisible(false);
  }, []);

  const getGaugeTooltipContent = useCallback(
    function() {
      return getBrandTooltipDetails({ data, brandIndex: 0, t, language: i18n.language });
    },
    [data, t, i18n.language]
  );

  const isControlled = deepDiveData !== undefined;
  const currentDeepDiveData = isControlled
    ? deepDiveData
    : internalDeepDiveData;

  // Calculate dynamic height based on number of items
  const calculateChartHeight = (itemCount, isMainChart = false) => {
    if (isMainChart) {
      const minHeight = 300;
      const itemHeight = 45; // Height per bar item
      const padding = 100; // Extra space for legends, labels, etc.
      return Math.max(minHeight, itemCount * itemHeight + padding);
    } else {
      // For topic charts - more compact (decreased height)
      const minHeight = 100; // Reduced from 180
      const itemHeight = 16; // Reduced from 25
      const padding = 30; // Reduced from 50
      return Math.max(minHeight, itemCount * itemHeight + padding);
    }
  };

  // Truncate long labels
  const truncateLabel = (label, maxLength = 15) => {
    if (!label) return "";
    return label.length > maxLength
      ? `${label.substring(0, maxLength)}...`
      : label;
  };

  const handleBarClick = useCallback(
    async (event, elements) => {
      // NOTE: This is the MAIN (brand) chart click — does NOT open the drawer.
      if (!elements || elements.length === 0 || isLoading) {
        return;
      }
      const clickedElement = elements[0];
      const dataIndex = clickedElement.index;
      const brandLabel = data?.brandsScore?.labels?.[dataIndex];
      const brandId =
        data?.brandsScore?.brandIds?.[dataIndex] ||
        data?.brandsScore?.metadata?.ids?.[dataIndex];
      if (onBrandClick) {
        onBrandClick(brandId, brandLabel, data.topicsScore);
      } else {
        // Fallback for uncontrolled mode (Welcome Page)
        setIsLoading(true);
        setSelectedBrandLabel(brandLabel);
        // Simulate loading
        await new Promise((resolve) => setTimeout(resolve, 500));
        if (data?.topicsScore) {
          setInternalDeepDiveData(data.topicsScore);
        }
        setIsLoading(false);
      }
    },
    [isLoading, data, onBrandClick, isControlled]
  );

  // NEW: Click handler for TOPIC charts to open side panel and fetch reviews
  const handleTopicBarClick = useCallback(
    async (_event, elements, ctx) => {
      if (!elements || elements.length === 0) return;
      const idx = elements[0]?.index;
      const topicName = ctx?.labels?.[idx];
      const brandId = ctx?.brandId;
      const brandName = ctx?.brandName;

      if (!topicName) return;

      // Check if we're already showing the same topic
      if (
        selectedIssue &&
        selectedIssue.label === topicName &&
        selectedIssue.brandId === brandId &&
        drawerOpen
      ) {
        return; // Already showing this topic
      }

      setSelectedIssue({ label: topicName, brandName, brandId });
      setDrawerOpen(true);
      setReviewsLoading(true);
      setReviewsStartIndex(0); // Reset to first page when opening new topic
      const payload = {
        topic_name: topicName,
        brand_id: brandId,
        topic_name: topicName,
        id: widget_id,
        source: source,
        since: since,
        until: until,
        start_index: 0,
        page_size: reviewsPageSize,
      };
      fetchTopProblems(payload);
    },
    [
      widget_id,
      source,
      since,
      until,
      reviewsPageSize,
      selectedIssue,
      drawerOpen,
    ]
  );

  const handleExitDeepDive = useCallback(() => {
    if (onExitDeepDive) {
      onExitDeepDive();
    } else {
      setInternalDeepDiveData(null);
      setSelectedBrandLabel(null);
    }
  }, [onExitDeepDive, isControlled]);

  // Cleanup effect to prevent memory leaks and unnecessary API calls
  useEffect(() => {
    return () => {
      // Cleanup when component unmounts
      setReviewsLoading(false);
    };
  }, []);

  const getMainChartOptions = useCallback(() => {
    const itemCount = data?.brandsScore?.labels?.length || 0;

    return {
      responsive: true,
      indexAxis: "x",
      animation: false,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: "nearest",
        axis: "x",
      },
      onHover: (event, elements) => {
        event.native.target.style.cursor =
          elements.length > 0 ? "pointer" : "default";
      },
      onClick: handleBarClick,
      scales: {
        x: {
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            padding: 10,
            color: theme === "dark" ? "white" : "black",
            font: {
              size: 12,
            },
            maxRotation: itemCount > 6 ? 45 : 0,
            minRotation: 0,
            autoSkip: false, // Only skip when many items
            callback: function(value) {
              const label = this.getLabelForValue(value);
              if (label && label.length > 20) {
                return label.substring(0, 20) + "...";
              }
              return label;
            },
          },
        },
        y: {
          min: 0,
          max: 5,
          grid: {
            display: true,
            drawBorder: false,
            color:
              theme === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)",
            lineWidth: 1,
          },
          ticks: {
            padding: 20,
            color: theme === "dark" ? "white" : "black",
            font: {
              size: 11,
            },
            stepSize: 1,
            callback: function(value) {
              // Only show integer values
              if (Number.isInteger(value)) {
                return value;
              }
              return null;
            },
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          color: "white",
          font: {
            size: 9,
          },
          formatter: (value) => {
            if (value < 1) {
              return "";
            }
            return Number(value).toFixed(1);
          },
          display: true,
          anchor: "center",
          align: "center",
          clip: true,
          clamp: true,
          textAlign: "center",
        },
        tooltip: {
          enabled: true,
          mode: "index",
          intersect: false,
          axis: "x",
          titleColor: theme === "dark" ? "white" : "black",
          bodyColor: theme === "dark" ? "white" : "black",
          backgroundColor:
            theme === "dark"
              ? "rgba(0, 0, 0, 0.8)"
              : "rgba(255, 255, 255, 0.9)",
          borderColor:
            theme === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.1)",
          borderWidth: 1,
          cornerRadius: 4,
          padding: 8,
          titleFont: {
            size: 12,
          },
          bodyFont: {
            size: 11,
          },
          callbacks: {
            title: (tooltipItems) => tooltipItems[0].label,
            label: (context) => {
              var brandIndex = context.dataIndex;
              var brandName = context.label;
              return getBrandTooltipDetails({
                data,
                brandIndex: brandIndex,
                brandNameOverride: brandName,
                t,
                language: i18n.language,
              });
            },
            afterLabel: () => "Click to see topic breakdown",
          },
        },
      },
      backgroundColor: "transparent",
      layout: {
        padding: {
          left: 10,
          right: 10,
          top: 15,
          bottom: 15,
        },
      },
    };
  }, [theme, handleBarClick, data]);
  // UPDATED: let topic charts open the drawer
  const getTopicChartOptions = useCallback(
    (
      itemCount = 0,
      brandCtx = { brandId: null, brandName: "", labels: [] }
    ) => ({
      responsive: true,
      indexAxis: "x",
      animation: false,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: "nearest",
        axis: "x",
      },
      onClick: (event, elements) =>
        handleTopicBarClick(event, elements, brandCtx),
      scales: {
        x: {
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            padding: 5,
            color: theme === "dark" ? "white" : "black",
            font: {
              size: 11,
            },
            stepSize: 1,
            autoSkip: false,
            callback: function(value) {
              const label = this.getLabelForValue(value);
              if (label && label.length > 20) {
                return label.substring(0, 20) + "...";
              }
              return label;
            },
          },
          beginAtZero: true,
          min: 0,
          max: itemCount - 1, // Dynamic max based on actual item count
        },
        y: {
          min: 0,
          max: 5,
          grid: {
            display: true,
            drawBorder: false,
            color:
              theme === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)",
            lineWidth: 1,
          },
          ticks: {
            beginAtZero: true,
            padding: 10,
            color: theme === "dark" ? "white" : "black",
            font: {
              size: 11,
            },
            stepSize: 1,
            autoSkip: false,
            callback: function(value, index) {
              const label = this.getLabelForValue(value);
              return truncateLabel(label, 20);
            },
          },
          categoryPercentage: Math.min(
            0.9,
            Math.max(0.4, 1 - itemCount * 0.02)
          ),
          barPercentage: Math.min(0.8, Math.max(0.3, 1 - itemCount * 0.015)),
        },
      },
      plugins: {
        legend: {
          display: false,
          position: "bottom",
          labels: {
            color: theme === "dark" ? "white" : "black",
            usePointStyle: false,
            boxWidth: 0,
            padding: 10,
            font: {
              size: 12,
            },
          },
        },
        datalabels: {
          color: "white",
          font: {
            size: 9,
          },
          formatter: (value) => {
            if (value < 1) {
              return "";
            }
            return Number(value).toFixed(1);
          },
          display: true,
          anchor: "center",
          align: "center",
          clip: true,
          clamp: true,
          textAlign: "center",
        },
        tooltip: {
          enabled: true,
          mode: "index",
          intersect: false,
          axis: "x",
          titleColor: theme === "dark" ? "white" : "black",
          bodyColor: theme === "dark" ? "white" : "black",
          backgroundColor:
            theme === "dark"
              ? "rgba(0, 0, 0, 0.8)"
              : "rgba(255, 255, 255, 0.9)",
          borderColor:
            theme === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.1)",
          borderWidth: 1,
          cornerRadius: 4,
          padding: 8,
          maxWidth: 400,
          maxHeight: 800,
          titleFont: {
            size: 12,
          },
          bodyFont: {
            size: 11,
          },
          callbacks: {
            title: (tooltipItems) => {
              return tooltipItems[0].label;
            },
            label: (context) => {
              // Always use the actual bar value for VOC Score (not the index)
              const score = context.dataset.data[context.dataIndex];
              const datasetLabel = context.dataset.label || "Topic";
              const topicName = context.label;
              let details = [
                `VOC Score: ${score !== undefined ? score.toFixed(1) : "N/A"}`,
              ];
              // Find the topic details - fixed logic for single brand mode
              let currentBrandData = null;

              if (isSingleBrand) {
                // For single brand mode, get data directly from data.topicsScore.allBrands[0]
                currentBrandData = data?.topicsScore?.allBrands?.[0];
              } else {
                // For multi-brand mode, find the brand by name
                currentBrandData = currentDeepDiveData?.allBrands?.find(
                  (brand) =>
                    brand.brandName === datasetLabel.replace(" Topics", "")
                );
              }
              if (currentBrandData?.data?.topicDetails) {
                const topicDetail = currentBrandData.data.topicDetails.find(
                  (topic) => topic.name === topicName
                );
                if (topicDetail) {
                  if (topicDetail.total_reviews != null) {
                    details.push(
                      `Total Reviews: ${topicDetail.total_reviews}`
                    );
                  }
                  if (
                    topicDetail.rating_weight != null &&
                    !Number.isNaN(Number(topicDetail.rating_weight))
                  ) {
                    details.push(
                      `Rating Weight: ${(
                        topicDetail.rating_weight * 100
                      ).toFixed(1)}% (the proportion of mobile app reviews)`
                    );
                  }
                  if (topicDetail.positive_percentage != null) {
                    details.push(
                      `Positive: ${Number(
                        topicDetail.positive_percentage
                      ).toFixed(1)}%`
                    );
                  }
                  if (topicDetail.appraisal_percentage != null) {
                    details.push(
                      `Appraisal: ${Number(
                        topicDetail.appraisal_percentage
                      ).toFixed(1)}%`
                    );
                  }
                  if (topicDetail.neutral_percentage != null) {
                    details.push(
                      `Neutral: ${Number(
                        topicDetail.neutral_percentage
                      ).toFixed(1)}%`
                    );
                  }
                  if (topicDetail.score != null) {
                    details.push(
                      `Topic score: ${Number(topicDetail.score).toFixed(1)}`
                    );
                  }
                  if (topicDetail.average_rating != null) {
                    details.push(
                      `Avg Rating: ${Number(topicDetail.average_rating).toFixed(
                        1
                      )}`
                    );
                  }
                  const engagement = topicDetail.engagement;
                  const engagementScore = topicDetail.engagement_score;
                  if (
                    engagementScore !== undefined &&
                    engagementScore !== null
                  ) {
                    if (engagement) {
                      details.push("Engagement:");
                      details.push(`  Likes: ${engagement?.likes ?? 0}`);
                      details.push(`  Retweets: ${engagement?.retweets ?? 0}`);
                      details.push(`  Replies: ${engagement?.replies ?? 0}`);
                      details.push(`  Views: ${engagement?.views ?? 0}`);
                    }
                    details.push(`Engagement Score: ${engagementScore}`);
                  } else {
                    details.push("No engagement");
                  }
                }
              }
              return details.filter(Boolean);
            },
          },
        },
      },
      backgroundColor: "transparent",
      layout: {
        padding: {
          left: 0,
          right: 8,
          top: 10,
          bottom: 10,
        },
      },
    }),
    [theme, currentDeepDiveData, isSingleBrand, data, handleTopicBarClick]
  );

  // Render deep dive navigation
  const renderDeepDiveNavigation = () => {
    const brandLabel = onBrandClick
      ? deepDiveData?.brandName
      : selectedBrandLabel;
    return (
      <div className={styles.deepDiveNavigation}>
        <Tag
          className={styles.deepDiveTag}
          closable
          closeIcon={<CloseOutlined style={{ color: "white" }} />}
          onClose={handleExitDeepDive}
        >
          Topic Breakdown
        </Tag>
      </div>
    );
  };

  // Validate data and render appropriate content
  if (
    !data ||
    !data?.brandsScore ||
    !data?.brandsScore?.labels ||
    data?.brandsScore.labels.length === 0
  ) {
    console.error("VOCScoreChart: Invalid data structure", data);
    return (
      <div>
        <div className={styles.errorContainer}>
          <div>
            <h4>No data available</h4>
          </div>
        </div>
      </div>
    );
  }

  // Render loading state
  if (isLoading) {
    return (
      <div>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <p>Loading brand topic data...</p>
        </div>
      </div>
    );
  }

  if (isSingleBrand) {
    const deepDiveContent = data?.topicsScore?.allBrands;
    const brandVocScore = data?.brandsScore?.datasets[0].data[0] || 0;

    return (
      <div>
        <div
          className={styles.gaugeChart}
          onMouseEnter={handleGaugeMouseEnter}
          onMouseLeave={handleGaugeMouseLeave}
        >
          <GaugeChart
            id={`gauge-chart-widget-${widget_id}`}
            animate={false}
            className={styles.sentimentGauge}
            nrOfLevels={3}
            textColor={theme === "dark" ? "white" : "black"}
            percent={""}
            width={100}
            colors={["#AA2B0E", "#FFA800", "#00B07B"]}
            arcWidth={0.25}
            hideText={true}
            needleColor="transparent"
            needleBaseColor="transparent"
          />
          <span
            className={`${styles.gaugeIndicator} printable-gauge-indicator`}
          >
            {brandVocScore}
          </span>
          {gaugeTooltipVisible && (
            <div className={styles.gaugeTooltip}>
              {getGaugeTooltipContent().map(function(detail, index) {
                return (
                  <div key={index} style={{ marginBottom: "2px" }}>
                    {detail}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <span>Topic Breakdown</span>

        {deepDiveContent && deepDiveContent.length > 0 ? (
          deepDiveContent.map((brandChart, index) => {
            // Ensure all topics are shown, even if data is empty or zero
            const itemCount = brandChart?.data?.labels?.length || 0;
            const chartHeight = calculateChartHeight(itemCount);
            // If data is missing, fill with zeros
            let chartData = brandChart?.data;
            if (
              chartData &&
              chartData.labels &&
              chartData.datasets &&
              chartData.datasets.length > 0
            ) {
              chartData = {
                ...chartData,
                datasets: chartData.datasets.map((ds) => ({
                  ...ds,
                  data: chartData.labels.map((_, i) => ds.data[i] ?? 0),
                })),
              };
            }
            return (
              <div
                key={brandChart?.brandId || index}
                className={`${styles.brandChartContainer} ${styles.topicChartContainer} ${styles.gridItem}`}
              >
                <div
                  className={`${styles.chartWrapper} ${styles.topicChart} ${
                    itemCount > 7 ? styles.scrollableChart : ""
                  }`}
                  style={{
                    width: "100%",
                    height: chartHeight,
                    overflow: "auto", // Enable scrolling for both axes
                    minHeight: "300px",
                    maxWidth: "100%", // Ensure it doesn't exceed container width
                  }}
                  title={
                    itemCount > 7 ? "Scroll horizontally to see all topics" : ""
                  }
                >
                  <CustomChart
                    type="bar"
                    data={chartData}
                    options={getTopicChartOptions(itemCount, {
                      brandId: brandChart?.brandId,
                      brandName: brandChart?.brandName,
                      labels: chartData?.labels || [],
                    })}
                    theme={theme}
                    width={Math.max(700, itemCount * 100)} // Dynamic width based on item count
                    height={Math.max(350, itemCount * 50)} // Dynamic height based on item count
                  />
                </div>
              </div>
            );
          })
        ) : (
          <div>No deep dive data available</div>
        )}

        {/* Side Panel Drawer (single brand mode) */}
        {/* Side Panel Drawer */}
        <Drawer
          title={
            selectedBarData
              ? selectedBarData.brand
              : "AI Problems Root Cause Analysis & Recommendations"
          }
          placement="right"
          closable
          onClose={handleCloseSidePanel}
          open={drawerOpen}
          width={650}
          closeIcon={
            <CloseOutlined
              style={{ color: theme === "dark" ? "white" : "black" }}
            />
          }
          styles={{
            body: {
              padding: 0,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            },
          }}
        >
          <div className={styles.sidePanelContainer}>
            {/* AI Problems Section */}
            <div className={styles.problemsSection}>
              <div className={styles.problemsHeader}>
                <div className={styles.problemsIcon}>?</div>
                <h3 className={styles.problemsTitle}>
                  AI Problems Root Cause Analysis & Recommendations
                </h3>
              </div>

              <p className={styles.problemsDescription}>
                Click a root cause to see the reviews associated
              </p>

              {/* Problems Table - Ant Design with Horizontal Scroll */}
              <div className={styles.problemsTableContainer}>
                <Table
                  dataSource={topProblems}
                  columns={[
                    {
                      title: "Area",
                      dataIndex: "label",
                      key: "label",
                      width: 100,
                      render: (text) => (
                        <span className={styles.areaCell}>
                          {text || "General"}
                        </span>
                      ),
                    },
                    {
                      title: "Root Cause",
                      dataIndex: "root_cause",
                      key: "root_cause",
                      width: 180,
                      render: (text, record) => {
                        const isSelected =
                          selectedProblem?.rc_id === record.rc_id ||
                          selectedProblem?.id === record.id;
                        return (
                          <div
                            className={`${styles.rootCauseCell} ${
                              isSelected ? styles.selected : ""
                            }`}
                            onClick={() => handleProblemClick(record)}
                          >
                            {text || record.problem}
                          </div>
                        );
                      },
                    },
                    {
                      title: "Recommendation",
                      dataIndex: "recommendation",
                      key: "recommendation",
                      width: 180,
                      render: (text) => (
                        <span className={styles.recommendationCell}>
                          {text || "No recommendation available"}
                        </span>
                      ),
                    },
                  ]}
                  loading={topProblemsLoading}
                  rowKey={(record, index) => record.rc_id || record.id || index}
                  pagination={false}
                  scroll={{ x: 400, y: 250 }}
                  size="small"
                  bordered={false}
                  style={{
                    fontSize: "11px",
                  }}
                  onRow={(record) => ({
                    style: {
                      background:
                        selectedProblem?.rc_id === record.rc_id ||
                        selectedProblem?.id === record.id
                          ? `rgba(99, 102, 241, 0.1)`
                          : "transparent",
                      borderLeft:
                        selectedProblem?.rc_id === record.rc_id ||
                        selectedProblem?.id === record.id
                          ? `3px solid var(--active)`
                          : "3px solid transparent",
                      transition: "all 0.2s ease",
                    },
                  })}
                />
              </div>
            </div>

            {/* Reviews Section - Only show when a root cause is selected */}
            {selectedProblem && (
              <div className={styles.reviewsSection}>
                <div className={styles.reviewsHeader}>
                  <h4 className={styles.reviewsTitle}>
                    Reviews -{" "}
                    {selectedProblem?.root_cause ||
                      selectedProblem?.problem ||
                      selectedProblem?.label ||
                      "Selected Root Cause"}
                  </h4>
                </div>

                {/* Reviews Content */}
                <div className={styles.reviewsContent}>
                  <div className={styles.reviewsListWrapper}>
                    <Spin spinning={reviewsLoading}>
                      {problemReviews && problemReviews.length > 0 ? (
                        <WelcomeDrawerReviewsList
                          allReviews={problemReviews}
                          reviewsLoading={reviewsLoading}
                          showTotalCount
                        />
                      ) : (
                        <div className={styles.noReviewsMessage}>
                          {reviewsLoading
                            ? "Loading reviews..."
                            : "No reviews found for this problem."}
                        </div>
                      )}
                    </Spin>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Drawer>
      </div>
    );
  }

  // Determine if we are in drill-down (deep dive) mode
  const isDrilledDown = !!currentDeepDiveData;

  // Render deep dive view with improved grid layout
  if (currentDeepDiveData) {
    const deepDiveContent = isControlled
      ? deepDiveData?.allBrands
      : currentDeepDiveData?.allBrands;

    return (
      <div className={styles.widgetcontent}>
        <div>{renderDeepDiveNavigation()}</div>
        <div className={styles.deepDiveContent}>
          <div
            className={`${styles.brandChartsGrid} ${styles.topicChartsGrid} ${styles.improvedGrid}`}
          >
            {deepDiveContent && deepDiveContent.length > 0 ? (
              deepDiveContent.map((brandChart, index) => {
                // Ensure all topics are shown, even if data is empty or zero
                const itemCount = brandChart?.data?.labels?.length || 0;

                const chartHeight = calculateChartHeight(itemCount);
                // If data is missing, fill with zeros
                let chartData = brandChart?.data;
                if (
                  chartData &&
                  chartData.labels &&
                  chartData.datasets &&
                  chartData.datasets.length > 0
                ) {
                  chartData = {
                    ...chartData,
                    datasets: chartData.datasets.map((ds) => ({
                      ...ds,
                      data: chartData.labels.map((_, i) => ds.data[i] ?? 0),
                    })),
                  };
                }
                return (
                  <div
                    key={brandChart?.brandId || index}
                    className={`${styles.brandChartContainer} ${styles.topicChartContainer} ${styles.gridItem}`}
                    style={{ minWidth: "600px", minHeight: "350px" }}
                  >
                    <h4
                      className={`${styles.brandChartTitle} ${styles.topicChartTitle}`}
                    >
                      {brandChart?.brandName}
                    </h4>
                    <div
                      className={`${styles.chartWrapper} ${styles.topicChart} ${
                        itemCount > 7 ? styles.scrollableChart : ""
                      }`}
                      style={{
                        width: "100%",
                        height: chartHeight,
                        overflow: "auto", // Enable scrolling for both axes
                        minHeight: "300px",
                        maxWidth: "100%", // Ensure it doesn't exceed container width
                      }}
                      title={
                        itemCount > 7
                          ? "Scroll horizontally to see all topics"
                          : ""
                      }
                    >
                      <CustomChart
                        type="bar"
                        data={chartData}
                        options={getTopicChartOptions(itemCount, {
                          brandId: brandChart?.brandId,
                          brandName: brandChart?.brandName,
                          labels: chartData?.labels || [],
                        })}
                        theme={theme}
                        width={Math.max(700, itemCount * 100)} // Dynamic width based on item count
                        height={Math.max(350, itemCount * 50)} // Dynamic height based on item count
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <div>No deep dive data available</div>
            )}
          </div>
        </div>

        {/* Side Panel Drawer (deep-dive multi-brand mode) */}
        {/* Side Panel Drawer */}
        <Drawer
          title={
            selectedBarData
              ? selectedBarData.brand
              : "AI Problems Root Cause Analysis & Recommendations"
          }
          placement="right"
          closable
          onClose={handleCloseSidePanel}
          open={drawerOpen}
          width={650}
          closeIcon={
            <CloseOutlined
              style={{ color: theme === "dark" ? "white" : "black" }}
            />
          }
          styles={{
            body: {
              padding: 0,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            },
          }}
        >
          <div className={styles.sidePanelContainer}>
            {/* AI Problems Section */}
            <div className={styles.problemsSection}>
              <div className={styles.problemsHeader}>
                <div className={styles.problemsIcon}>?</div>
                <h3 className={styles.problemsTitle}>
                  AI Problems Root Cause Analysis & Recommendations
                </h3>
              </div>

              <p className={styles.problemsDescription}>
                Click a root cause to see the reviews associated
              </p>

              {/* Problems Table - Ant Design with Horizontal Scroll */}
              <div className={styles.problemsTableContainer}>
                <Table
                  dataSource={topProblems}
                  columns={[
                    {
                      title: "Area",
                      dataIndex: "label",
                      key: "label",
                      width: 100,
                      render: (text) => (
                        <span className={styles.areaCell}>
                          {text || "General"}
                        </span>
                      ),
                    },
                    {
                      title: "Root Cause",
                      dataIndex: "root_cause",
                      key: "root_cause",
                      width: 180,
                      render: (text, record) => {
                        const isSelected =
                          selectedProblem?.rc_id === record.rc_id ||
                          selectedProblem?.id === record.id;
                        return (
                          <div
                            className={`${styles.rootCauseCell} ${
                              isSelected ? styles.selected : ""
                            }`}
                            onClick={() => handleProblemClick(record)}
                          >
                            {text || record.problem}
                          </div>
                        );
                      },
                    },
                    {
                      title: "Recommendation",
                      dataIndex: "recommendation",
                      key: "recommendation",
                      width: 180,
                      render: (text) => (
                        <span className={styles.recommendationCell}>
                          {text || "No recommendation available"}
                        </span>
                      ),
                    },
                  ]}
                  loading={topProblemsLoading}
                  rowKey={(record, index) => record.rc_id || record.id || index}
                  pagination={false}
                  scroll={{ x: 400, y: 250 }}
                  size="small"
                  bordered={false}
                  style={{
                    fontSize: "11px",
                  }}
                  onRow={(record) => ({
                    style: {
                      background:
                        selectedProblem?.rc_id === record.rc_id ||
                        selectedProblem?.id === record.id
                          ? `rgba(99, 102, 241, 0.1)`
                          : "transparent",
                      borderLeft:
                        selectedProblem?.rc_id === record.rc_id ||
                        selectedProblem?.id === record.id
                          ? `3px solid var(--active)`
                          : "3px solid transparent",
                      transition: "all 0.2s ease",
                    },
                  })}
                />
              </div>
            </div>

            {/* Reviews Section - Only show when a root cause is selected */}
            {selectedProblem && (
              <div className={styles.reviewsSection}>
                <div className={styles.reviewsHeader}>
                  <h4 className={styles.reviewsTitle}>
                    Reviews -{" "}
                    {selectedProblem?.root_cause ||
                      selectedProblem?.problem ||
                      selectedProblem?.label ||
                      "Selected Root Cause"}
                  </h4>
                </div>

                {/* Reviews Content */}
                <div className={styles.reviewsContent}>
                  <div className={styles.reviewsListWrapper}>
                    <Spin spinning={reviewsLoading}>
                      {problemReviews && problemReviews.length > 0 ? (
                        <WelcomeDrawerReviewsList
                          allReviews={problemReviews}
                          reviewsLoading={reviewsLoading}
                          showTotalCount
                        />
                      ) : (
                        <div className={styles.noReviewsMessage}>
                          {reviewsLoading
                            ? "Loading reviews..."
                            : "No reviews found for this problem."}
                        </div>
                      )}
                    </Spin>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Drawer>
      </div>
    );
  }

  // Render main brand VOC Score chart with dynamic height
  const mainChartItemCount = data?.brandsScore?.labels?.length || 0;
  console.log("Brand Chart Item Count:", mainChartItemCount);
  const mainChartHeight = calculateChartHeight(mainChartItemCount, true);
  return (
    <div
      className={styles.chartWrapper}
      style={{
        height: `${mainChartHeight}px`,
        transition: "height 0.3s ease",
        minHeight: "300px",
      }}
    >
      <div className={styles.chartBox}>
      <CustomChart
        type="bar"
        data={data?.brandsScore}
        options={getMainChartOptions()}
        theme={theme}
      />
      </div>
      {data?.brandsScore && (
        <HighestScoreComponent brandScoreInfo={data.brandsScore} theme={theme} />
      )}

      {/* Side Panel Drawer (idle state — will be used once deep dive happens) */}
      <Drawer
        title={
          selectedBarData
            ? selectedBarData.brand
            : "AI Problems Root Cause Analysis & Recommendations"
        }
        placement="right"
        closable
        onClose={handleCloseSidePanel}
        open={drawerOpen}
        width={650}
        closeIcon={
          <CloseOutlined
            style={{ color: theme === "dark" ? "white" : "black" }}
          />
        }
        styles={{
          body: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
        }}
      >
        <div className={styles.sidePanelContainer}>
          {/* AI Problems Section */}
          <div className={styles.problemsSection}>
            <div className={styles.problemsHeader}>
              <div className={styles.problemsIcon}>?</div>
              <h3 className={styles.problemsTitle}>
                AI Problems Root Cause Analysis & Recommendations
              </h3>
            </div>

            <p className={styles.problemsDescription}>
              Click a root cause to see the reviews associated
            </p>

            {/* Problems Table - Ant Design with Horizontal Scroll */}
            <div className={styles.problemsTableContainer}>
              <Table
                dataSource={topProblems}
                columns={[
                  {
                    title: "Area",
                    dataIndex: "label",
                    key: "label",
                    width: 100,
                    render: (text) => (
                      <span className={styles.areaCell}>
                        {text || "General"}
                      </span>
                    ),
                  },
                  {
                    title: "Root Cause",
                    dataIndex: "root_cause",
                    key: "root_cause",
                    width: 180,
                    render: (text, record) => {
                      const isSelected =
                        selectedProblem?.rc_id === record.rc_id ||
                        selectedProblem?.id === record.id;
                      return (
                        <div
                          className={`${styles.rootCauseCell} ${
                            isSelected ? styles.selected : ""
                          }`}
                          onClick={() => handleProblemClick(record)}
                        >
                          {text || record.problem}
                        </div>
                      );
                    },
                  },
                  {
                    title: "Recommendation",
                    dataIndex: "recommendation",
                    key: "recommendation",
                    width: 180,
                    render: (text) => (
                      <span className={styles.recommendationCell}>
                        {text || "No recommendation available"}
                      </span>
                    ),
                  },
                ]}
                loading={topProblemsLoading}
                rowKey={(record, index) => record.rc_id || record.id || index}
                pagination={false}
                scroll={{ x: 400, y: 250 }}
                size="small"
                bordered={false}
                style={{
                  fontSize: "11px",
                }}
                onRow={(record) => ({
                  style: {
                    background:
                      selectedProblem?.rc_id === record.rc_id ||
                      selectedProblem?.id === record.id
                        ? `rgba(99, 102, 241, 0.1)`
                        : "transparent",
                    borderLeft:
                      selectedProblem?.rc_id === record.rc_id ||
                      selectedProblem?.id === record.id
                        ? `3px solid var(--active)`
                        : "3px solid transparent",
                    transition: "all 0.2s ease",
                  },
                })}
              />
            </div>
          </div>

          {/* Reviews Section - Only show when a root cause is selected */}
          {selectedProblem && (
            <div className={styles.reviewsSection}>
              <div className={styles.reviewsHeader}>
                <h4 className={styles.reviewsTitle}>
                  Reviews -{" "}
                  {selectedProblem?.root_cause ||
                    selectedProblem?.problem ||
                    selectedProblem?.label ||
                    "Selected Root Cause"}
                </h4>
              </div>

              {/* Reviews Content */}
              <div className={styles.reviewsContent}>
                <div className={styles.reviewsListWrapper}>
                  <Spin spinning={reviewsLoading}>
                    {problemReviews && problemReviews.length > 0 ? (
                      <WelcomeDrawerReviewsList
                          allReviews={problemReviews}
                          reviewsLoading={reviewsLoading}
                          showTotalCount
                        />
                    ) : (
                      <div className={styles.noReviewsMessage}>
                        {reviewsLoading
                          ? "Loading reviews..."
                          : "No reviews found for this problem."}
                      </div>
                    )}
                  </Spin>
                </div>
              </div>
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default VOCScoreChart;
